import type { MutableRefObject, ReactNode, TransitionEvent } from 'react';
import { useRef, useState } from 'react';

import { useResizeObserver } from '@genially/document-components/build/hooks/useResizeObserver';
import { calculate } from '@genially/utils';

import { AnimatedSidebarSummary } from './components/AnimatedSidebarSummary/AnimatedSidebarSummary';
import { SidebarSummaryWithContent } from './components/SidebarSummaryWithContent/SidebarSummaryWithContent';
import { useSidebarBehaviour } from './hooks/useSidebarBehaviour';
import { SidebarType } from './types/SidebarType';
import { useSidebarSummaryContext } from './SidebarSummary.context';
import { SidebarWrapper, Wrapper } from './SidebarSummary.styled';

interface SidebarSummaryProps {
  LoadingComponent?: ReactNode;
  isLoading?: boolean;
}

export const SidebarSummary = ({ LoadingComponent, isLoading }: SidebarSummaryProps) => {
  const {
    animation,
    state: { isOpen },
    type,
  } = useSidebarSummaryContext();

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isRankingType = type === SidebarType.Ranking;

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  const {
    showComponent,
    showLastAnimation,
    handleAnimationEnd,
    isFullWidthEnabled: isFullWidthActive,
    handleClose,
    handleMaximizeMinimize,
    handleRefresh,
    handleSidebarTransitionEnd,
  } = useSidebarBehaviour();

  const resizeObserverCallback = () => {
    setViewportWidth(window.innerWidth);
  };

  const ref: MutableRefObject<HTMLBodyElement | null> =
    useResizeObserver(resizeObserverCallback);

  ref.current = document.body as HTMLBodyElement;

  const shouldShowRankingHeader = isRankingType && !isFullWidthActive;

  const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.target !== wrapperRef.current) {
      return;
    }

    handleSidebarTransitionEnd();
  };

  const ContentComponent = calculate(() => {
    if (isLoading) {
      return LoadingComponent;
    }

    if (showComponent) {
      return (
        <SidebarSummaryWithContent
          showLastAnimation={showLastAnimation}
          isFullWidthActive={isFullWidthActive}
          handleClose={handleClose}
          handleMaximizeMinimize={handleMaximizeMinimize}
          handleRefresh={handleRefresh}
        />
      );
    }

    return (
      <AnimatedSidebarSummary
        isFullWidthActive={isFullWidthActive}
        hideAnimationFinished={animation.finished}
        handleAnimationEnd={handleAnimationEnd}
        showLastAnimation={showLastAnimation}
      />
    );
  });

  return (
    <Wrapper
      ref={wrapperRef}
      $isOpen={isOpen}
      onTransitionEnd={handleTransitionEnd}
      $viewportWidth={viewportWidth}
    >
      <SidebarWrapper
        $isOpen={isOpen}
        $hasPaddings={showComponent}
        $isFullWidth={isOpen && isFullWidthActive}
        $hasReducedPadding={isRankingType}
        $activeAnimation={showComponent}
        $viewportWidth={viewportWidth}
        $removeSidePaddings={shouldShowRankingHeader}
      >
        {ContentComponent}
      </SidebarWrapper>
    </Wrapper>
  );
};
